import { useState } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { ConsignmentDetails, ReturnDetails } from 'types';
import { CopyButton, Heading, Timer } from 'components';
import { formatTimestamp } from 'helpers';
import {
  FontWeight,
  RETURN_STATUSES,
  STATUSES,
  TEST_IDS,
  salesMode,
} from 'commons';
import * as S from '../styles';

interface Props {
  consignment: ConsignmentDetails;
  returnDetails: ReturnDetails;
  defaultOpen?: boolean;
}

const UNKNOWN_STATUS = 'Status desconhecido';

export function ReturnHeader({
  consignment,
  returnDetails,
  defaultOpen = false,
}: Props) {
  const [showMore, setShowMore] = useState(defaultOpen);
  const {
    status,
    reservedEmployee,
    endDate,
    createdDate,
    customer,
    vouchers,
    nfeNumber,
  } = consignment;
  const { postOffice, protocol, status: returnStatus, option } = returnDetails;
  const { code: postOfficeCode } = postOffice;
  const hasVoucher = Boolean(vouchers[0]);
  const voucherCode = hasVoucher ? vouchers[0].code : 'Não aplicado';
  const voucherName = hasVoucher ? vouchers[0].name : '';

  const { day, month, hour, minute } = formatTimestamp(createdDate);
  const formattedCreatedDate = `${day}/${month} às ${hour}h${minute}`;

  const isPickup = consignment.salesMode === salesMode.pickup;
  const salesModeText = isPickup ? 'Retire na loja' : 'Entrega pela loja';

  const showReservedEmployee = Boolean(reservedEmployee?.name);

  const showPostOfficeCode =
    postOffice && postOfficeCode && option.type === 'POST_OFFICE';

  const showTimerStatuses = [
    STATUSES.AVAILABLE_FOR_STORE.value,
    STATUSES.RESERVED_BY_STORE.value,
  ];
  const showTimer = showTimerStatuses.includes(status);
  const customerName = `${customer.firstName} ${customer.lastName}`;

  function handleShowMore() {
    setShowMore(!showMore);
  }

  return (
    <S.HeaderWrapper $status={status} $returnStatus={returnStatus}>
      <S.StatusBar />

      <S.Main data-testid={TEST_IDS.CONSIGNMENT_DETAILS_HEADER}>
        <S.Header>
          <S.CodeWrapper>
            <Heading weight={FontWeight.bold}>{protocol}</Heading>

            <CopyButton
              data-testid={TEST_IDS.COPY_CONSIGMENT_CODE_BUTTON}
              content={protocol}
            />
          </S.CodeWrapper>

          <S.LargeWrapper>
            <S.StatusWrapper>
              <p>Status</p>

              <S.Status data-testid={TEST_IDS.CONSIGNMENT_STATUS_TEXT}>
                {returnStatus
                  ? RETURN_STATUSES[returnStatus]?.label || UNKNOWN_STATUS
                  : STATUSES[status]?.label || UNKNOWN_STATUS}
              </S.Status>
            </S.StatusWrapper>

            <S.Container>
              {showTimer && <Timer endTimer={endDate} />}

              <S.Button $showMore={showMore} onClick={handleShowMore}>
                <IoIosArrowDown />
              </S.Button>
            </S.Container>
          </S.LargeWrapper>
        </S.Header>

        {showMore && (
          <S.Retractable>
            <S.FirstContainer>
              <S.TextContainer>
                <S.BoldText>{salesModeText}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Cliente: </p>
                <S.BoldText>{customerName}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>NF: </p>
                <S.BoldText>{nfeNumber}</S.BoldText>
              </S.TextContainer>

              <S.TextContainer>
                <p>Pedido em:</p>
                <S.BoldText>{formattedCreatedDate}</S.BoldText>
              </S.TextContainer>

              {showReservedEmployee && (
                <S.TextContainer>
                  <p>Reservado:</p>
                  <S.BoldText>{reservedEmployee.name}</S.BoldText>
                </S.TextContainer>
              )}
            </S.FirstContainer>

            <S.Column>
              <S.TextContainer>
                <p>Cupom:</p>
                <S.TextContainer $column>
                  <S.BoldText>{voucherCode}</S.BoldText>

                  {hasVoucher && <S.BoldText>{voucherName}</S.BoldText>}
                </S.TextContainer>
              </S.TextContainer>

              {showPostOfficeCode && (
                <S.TextContainer>
                  <p>Código de postagem:</p>
                  <S.TextContainer $column>
                    <S.BoldText>{postOfficeCode}</S.BoldText>
                  </S.TextContainer>
                </S.TextContainer>
              )}
            </S.Column>
          </S.Retractable>
        )}
      </S.Main>
    </S.HeaderWrapper>
  );
}
